import { baseApi } from "@/store/baseApi";

export type SubmitVendorReviewRequest = {
  overallRating: number;
  shipTimeRating: number | null;
  accuracyRating: number | null;
  shippingChargeRating: number | null;
  textReview: string;
  recommendation: boolean | null;
};

export type VendorReviewSubmitResponse = {
  reviewId: number;
  vendorId: number;
  verId: number;
};

export const vendorApi = baseApi
  .enhanceEndpoints({
    addTagTypes: ["VendorPendingReviews", "VendorPublishedReviews"],
  })
  .injectEndpoints({
    endpoints: (builder) => ({
      getFulfillmentPolicyDefaults: builder.query<GetFulfillmentPolicyDefaultsResponse | null, number | undefined>({
        query: (vendorId) => `marketplace/vendor/${vendorId}/fulfillment-policies`,
      }),
      getPendingVendorReviews: builder.query<Page<PendingVendorReviewVO>, GetVendorReviewsPageArgs | void>({
        query: (args) => {
          const { pageNumber = 0, pageSize = 50 } = args ?? {};
          return {
            url: "neo/vendor/reviews/pending",
            params: { verId: process.env.NEXT_PUBLIC_VERTICAL_ID, pageNumber, pageSize },
          };
        },
        providesTags: [{ type: "VendorPendingReviews", id: "LIST" }],
      }),
      getPublishedVendorReviews: builder.query<Page<PublishedVendorReviewVO>, GetVendorReviewsPageArgs | void>({
        query: (args) => {
          const { pageNumber = 0, pageSize = 50 } = args ?? {};
          return {
            url: "neo/vendor/reviews/published",
            params: { verId: process.env.NEXT_PUBLIC_VERTICAL_ID, pageNumber, pageSize },
          };
        },
        providesTags: [{ type: "VendorPublishedReviews", id: "LIST" }],
      }),
      submitVendorReview: builder.mutation<
        VendorReviewSubmitResponse,
        { vendorId: number; customerOrderNumber: number; body: SubmitVendorReviewRequest }
      >({
        query: ({ vendorId, customerOrderNumber, body }) => ({
          url: `neo/vendor/${vendorId}/customer-orders/${customerOrderNumber}/reviews/submit`,
          method: "POST",
          params: { verId: process.env.NEXT_PUBLIC_VERTICAL_ID },
          body,
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        }),
        invalidatesTags: [{ type: "VendorPublishedReviews", id: "LIST" }],
      }),
      updateVendorReview: builder.mutation<
        VendorReviewSubmitResponse,
        { reviewId: number; body: SubmitVendorReviewRequest }
      >({
        query: ({ reviewId, body }) => ({
          url: `neo/vendor/reviews/${reviewId}`,
          method: "PUT",
          params: { verId: process.env.NEXT_PUBLIC_VERTICAL_ID },
          body,
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        }),
        invalidatesTags: [{ type: "VendorPublishedReviews", id: "LIST" }],
      }),
    }),
  });

export const {
  useLazyGetFulfillmentPolicyDefaultsQuery,
  useLazyGetPendingVendorReviewsQuery,
  useLazyGetPublishedVendorReviewsQuery,
  useGetPendingVendorReviewsQuery,
  useGetPublishedVendorReviewsQuery,
  useSubmitVendorReviewMutation,
  useUpdateVendorReviewMutation,
  util: { getRunningQueriesThunk },
} = vendorApi;

export const {
  getFulfillmentPolicyDefaults,
  getPendingVendorReviews,
  getPublishedVendorReviews,
  submitVendorReview,
  updateVendorReview,
} = vendorApi.endpoints;

enum FulfillmentPolicy {
  STOCKED = 1,
  MFR_ORDER = 2,
  DROP_SHIP = 3,
}

type GetFulfillmentPolicyDefaultsResponse = Array<{
  fulfillmentPolicyId: FulfillmentPolicy;
  promisedHandlingTime: number;
  minPromisedHandlingTime: number;
  maxPromisedHandlingTime: number;
}>;

export interface PendingReviewLineItemVO {
  oliId: number | null;
  vpId: number | null;
  mpId: number | null;
  mpName: string | null;
  productDetailLink: string | null;
  thumbnailImagePath: string | null;
  smallPhotoPath: string | null;
  mediaPath: string | null;
}

export type PublishedReviewLineItemVO = PendingReviewLineItemVO;

export interface PendingVendorReviewVO {
  vendorId: number;
  vendorName: string;
  lastOrderTime: string | null;
  lastCoId: number | null;
  lastOrderLineItems: PendingReviewLineItemVO[];
}

export interface PublishedVendorReviewVO {
  reviewId: number;
  vendorId: number;
  reviewTime: string | null;
  approvalTime: string | null;
  approver: number | null;
  comment: string | null;
  summary: string | null;
  generalRating: number | null;
  shippingTimeRating: number | null;
  shippingChargeRating: number | null;
  productAsDescribedRating: number | null;
  reviewerId: number | null;
  vendorName: string | null;
  coId: number | null;
  orderLineItems: PublishedReviewLineItemVO[];
  activeCd: number | null;
  recommendation: boolean | null;
}

interface GetVendorReviewsPageArgs {
  pageNumber?: number;
  pageSize?: number;
}
